import { useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { PermissionResponse } from '@/types/role';
import {
  buildPermissionMatrix,
  permissionActionColumn,
  type PermissionActionColumn,
  type PermissionMatrixModule,
} from '@/utils/permissionMatrix';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';

const ACTION_COLUMNS: PermissionActionColumn[] = [
  'CREATE',
  'VIEW',
  'UPDATE',
  'DELETE',
  'APPROVE',
  'EXPORT',
  'MANAGE',
];

type Props = {
  permissions: PermissionResponse[];
  enabledModules: string[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
};

export function PermissionMatrix({ permissions, enabledModules, selectedIds, onChange }: Props) {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const matrix = useMemo(
    () => buildPermissionMatrix(permissions, enabledModules),
    [permissions, enabledModules],
  );

  const filteredMatrix = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return matrix;
    return matrix
      .map((mod) => ({
        ...mod,
        permissions: mod.permissions.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.code.toLowerCase().includes(q) ||
            mod.label.toLowerCase().includes(q),
        ),
      }))
      .filter((mod) => mod.permissions.length > 0);
  }, [matrix, search]);

  const togglePermission = (id: number) => {
    onChange(
      selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id],
    );
  };

  const setModulePermissions = (mod: PermissionMatrixModule, selected: boolean) => {
    const modIds = mod.permissions.map((p) => p.id);
    if (selected) {
      onChange([...new Set([...selectedIds, ...modIds])]);
    } else {
      onChange(selectedIds.filter((id) => !modIds.includes(id)));
    }
  };

  const setActionPermissions = (mod: PermissionMatrixModule, action: PermissionActionColumn, selected: boolean) => {
    const actionIds = mod.byAction[action].map((p) => p.id);
    if (selected) {
      onChange([...new Set([...selectedIds, ...actionIds])]);
    } else {
      onChange(selectedIds.filter((id) => !actionIds.includes(id)));
    }
  };

  const selectAll = () => {
    const all = filteredMatrix.flatMap((m) => m.permissions.map((p) => p.id));
    onChange([...new Set([...selectedIds, ...all])]);
  };

  const unselectAll = () => {
    const all = new Set(filteredMatrix.flatMap((m) => m.permissions.map((p) => p.id)));
    onChange(selectedIds.filter((id) => !all.has(id)));
  };

  const moduleFullySelected = (mod: PermissionMatrixModule) =>
    mod.permissions.length > 0 && mod.permissions.every((p) => selectedIds.includes(p.id));

  const actionSelected = (mod: PermissionMatrixModule, action: PermissionActionColumn) => {
    const items = mod.byAction[action];
    return items.length > 0 && items.every((p) => selectedIds.includes(p.id));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1">
          <Input
            label="Search permissions"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Module, name, or code"
          />
        </div>
        <Button variant="secondary" type="button" onClick={selectAll}>Select all</Button>
        <Button variant="secondary" type="button" onClick={unselectAll}>Unselect all</Button>
      </div>

      <div className="max-h-[28rem] space-y-3 overflow-y-auto">
        {filteredMatrix.map((mod) => {
          const isCollapsed = collapsed[mod.key] ?? false;
          return (
            <div
              key={mod.key}
              className="rounded-lg border border-slate-200 dark:border-slate-700"
            >
              <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100"
                  onClick={() => setCollapsed((c) => ({ ...c, [mod.key]: !isCollapsed }))}
                >
                  {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                  {mod.label}
                  <span className="text-xs font-normal text-slate-500">
                    ({mod.permissions.filter((p) => selectedIds.includes(p.id)).length}/{mod.permissions.length})
                  </span>
                </button>
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={moduleFullySelected(mod)}
                    onChange={(e) => setModulePermissions(mod, e.target.checked)}
                  />
                  All module
                </label>
              </div>

              {!isCollapsed && (
                <div className="overflow-x-auto p-3">
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wide text-slate-500">
                        <th className="pb-2 pr-3">Action</th>
                        {ACTION_COLUMNS.map((col) => (
                          <th key={col} className="pb-2 px-1 text-center">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-1 pr-3 text-xs text-slate-500">Toggle column</td>
                        {ACTION_COLUMNS.map((col) => (
                          <td key={col} className="px-1 text-center">
                            {mod.byAction[col].length > 0 && (
                              <Tooltip content={`Toggle all ${col} in ${mod.label}`}>
                                <input
                                  type="checkbox"
                                  checked={actionSelected(mod, col)}
                                  onChange={(e) => setActionPermissions(mod, col, e.target.checked)}
                                  aria-label={`Toggle all ${col} in ${mod.label}`}
                                />
                              </Tooltip>
                            )}
                          </td>
                        ))}
                      </tr>
                      {mod.permissions.map((p) => {
                        const col = permissionActionColumn(p.code);
                        return (
                          <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800">
                            <td className="py-2 pr-3">
                              <span className="font-medium text-slate-800 dark:text-slate-200">{p.name}</span>
                              <span className="block text-xs text-slate-500">{p.code}</span>
                            </td>
                            {ACTION_COLUMNS.map((actionCol) => (
                              <td key={actionCol} className="px-1 text-center">
                                {actionCol === col ? (
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.includes(p.id)}
                                    onChange={() => togglePermission(p.id)}
                                  />
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
